// StackDetailSheet — drill-in for a "×N" grouped kitchen card.
//
// Mounted by KitchenScreen when the user taps a count badge on a
// stacked KitchenCard. Lists the N physical members of the stack
// with their per-instance state (current amount vs max, expiry,
// shelf location). Tapping a member opens the full MCMItemCard
// editor for THAT specific row.
//
// Aesthetic + structure mirror BrandPickerSheet (bottom-anchored,
// blur backdrop, theme-aware glass surface). The data model stays
// 1-row-per-physical-package — this sheet exists purely so the
// kitchen grid can render one tidy card per identity while still
// letting the user act on each individual can / box / bottle.

import { useEffect } from "react";
import { motion } from "framer-motion";
import { useTheme, THEME_TRANSITION } from "./theme";
import { font } from "./tokens";
import { buildDisplayName, formatDaysChip, daysChipColor } from "./helpers";
import { summarizeStack } from "../../lib/kitchenGrouping";

export function StackDetailSheet({ members, onOpenMember, onClose }) {
  const { theme } = useTheme();

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!Array.isArray(members) || members.length === 0) return null;

  const summary = summarizeStack(members);
  const leader = members[0];
  // Display name derives from the leader's raw row — same
  // composition the grid card uses, so the sheet header matches
  // what the user tapped from.
  const displayName = leader._raw
    ? buildDisplayName(leader._raw)
    : (leader.name || "Stack");

  const partialText = summary.partial > 0 && summary.full > 0
    ? `${summary.partial} partial · ${summary.full} full`
    : summary.partial > 0
      ? `${summary.partial} ${summary.partial === 1 ? "partial" : "partials"}`
      : `${summary.full} ${summary.full === 1 ? "full" : "full"}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${members.length} of ${displayName}`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 70,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: "rgba(20,12,4,0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
    >
      <motion.div
        initial={{ y: 32, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 360, damping: 32 }}
        style={{
          width: "100%",
          maxWidth: 520,
          maxHeight: "78vh",
          margin: "0 12px 24px",
          padding: "20px 18px 14px",
          borderRadius: 20,
          background: theme.color.glassFillHeavy,
          border: `1px solid ${theme.color.glassBorder}`,
          backdropFilter: "blur(12px) saturate(140%)",
          WebkitBackdropFilter: "blur(12px) saturate(140%)",
          boxShadow: "0 24px 60px rgba(20,12,4,0.40), 0 4px 16px rgba(20,12,4,0.20)",
          display: "flex", flexDirection: "column",
          ...THEME_TRANSITION,
        }}
      >
        {/* Kicker — count + small grey */}
        <div style={{
          fontFamily: font.mono, fontSize: 12,
          letterSpacing: "0.16em", textTransform: "uppercase",
          color: theme.color.inkMuted,
          fontWeight: 600,
        }}>
          ×{members.length} OF
        </div>
        <div style={{
          fontFamily: font.itemTitle, fontStyle: "italic", fontWeight: 500,
          fontSize: 22, color: theme.color.ink,
          marginTop: 4, marginBottom: 4, letterSpacing: "-0.01em",
          textTransform: "capitalize",
        }}>
          {displayName}
        </div>
        <div style={{
          fontFamily: font.sans, fontSize: 13,
          color: theme.color.inkMuted, marginBottom: 14,
        }}>
          {partialText}
          {summary.earliestExpiry && (
            <span> · earliest expires {formatDaysChip(summary.earliestExpiry)}</span>
          )}
        </div>

        {/* Member list — scrollable. Each row shows per-instance
            state. Tap to open MCMItemCard for that specific row. */}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex", flexDirection: "column", gap: 8,
          paddingRight: 4,
        }}>
          {members.map((m, idx) => {
            const amount = Number(m.amount);
            const max = Number(m.max);
            const pct = Number.isFinite(amount) && Number.isFinite(max) && max > 0
              ? Math.max(0, Math.min(1, amount / max))
              : null;
            const isPartial = pct != null && pct < 1;
            const exp = m._raw?.expiresAt || m.expiresAt || null;
            const expChipText = exp ? formatDaysChip(exp) : null;
            const expChipColor = exp ? daysChipColor(exp, theme) : theme.color.inkMuted;
            const qtyText = m.qty || (amount != null ? `${amount}` : null);
            return (
              <button
                key={m.id || idx}
                type="button"
                onClick={() => {
                  if (m._raw && onOpenMember) {
                    onOpenMember(m._raw);
                  }
                }}
                className="mcm-focusable"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  borderRadius: 12,
                  background: theme.color.glassFill || "transparent",
                  border: `1px solid ${theme.color.hairline}`,
                  textAlign: "left",
                  cursor: m._raw ? "pointer" : "default",
                  color: theme.color.ink,
                }}
              >
                {/* Number badge — which physical item this is */}
                <div style={{
                  flex: "0 0 auto",
                  width: 28, height: 28, borderRadius: 14,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: theme.color.glassFillHeavy,
                  border: `1px solid ${theme.color.hairline}`,
                  fontFamily: font.mono, fontSize: 12, fontWeight: 600,
                  color: theme.color.inkMuted,
                }}>
                  {idx + 1}
                </div>

                {/* Per-instance state column */}
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                  <div style={{
                    fontFamily: font.sans, fontSize: 13, fontWeight: 600,
                    color: theme.color.ink,
                    display: "flex", alignItems: "center", gap: 8,
                  }}>
                    {qtyText || "—"}
                    {isPartial && (
                      <span style={{
                        fontFamily: font.mono, fontSize: 12, fontWeight: 600,
                        letterSpacing: "0.10em", textTransform: "uppercase",
                        color: theme.color.gold || theme.color.inkMuted,
                      }}>
                        opened
                      </span>
                    )}
                  </div>
                  {expChipText && (
                    <div style={{
                      fontFamily: font.mono, fontSize: 12,
                      letterSpacing: "0.08em",
                      color: expChipColor,
                    }}>
                      {expChipText}
                    </div>
                  )}
                </div>

                {/* Chevron — affords tap */}
                <div style={{
                  flex: "0 0 auto",
                  fontFamily: font.mono, fontSize: 14,
                  color: theme.color.inkFaint,
                }}>
                  →
                </div>
              </button>
            );
          })}
        </div>

        {/* Close button — bottom row, matches BrandPickerSheet's
            "Cancel" affordance for keyboard-less dismissal. */}
        <button
          type="button"
          onClick={onClose}
          style={{
            alignSelf: "stretch",
            marginTop: 12,
            padding: "10px 12px",
            background: "transparent",
            border: `1px solid ${theme.color.hairline}`,
            borderRadius: 10,
            fontFamily: font.mono, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.10em", textTransform: "uppercase",
            color: theme.color.inkMuted,
            cursor: "pointer",
          }}
        >
          Done
        </button>
      </motion.div>
    </div>
  );
}
